import { PageTypeStatusMap, PageTypes, TransactionActionType } from "../types";

import { Direction, TransactionWrap } from "~/hooks/use-transaction";
import { getRequestStatusAndProgress } from "~/templates/transactions/components/TransactionDetailsInfo/utils/getRequestStatus";

export const processTransactionStatus = (status: string | undefined) => {
  switch (status) {
    case "PENDING":
      return "SENT";
    case "SENT":
      return "SENT";
    // When we set the status to ALL, we want to reset the direction
    case undefined:
      return status;
    default:
      return status;
  }
};

export const filterStatusesFromPageType = (
  pageType: PageTypes,
  statuses: { label: string; value: string | undefined }[],
) => {
  const validStatuses = PageTypeStatusMap[pageType.toUpperCase() as keyof typeof PageTypeStatusMap].split(",");
  const validFilterOptions = statuses.filter((status) => !status?.value || validStatuses.includes(status.value));
  const sentOption = validFilterOptions.find((f) => f.value === "SENT");
  if (sentOption) {
    sentOption.label = pageType === PageTypes.INCOMING_INBOX ? "Pending" : "Sent";
  }
  return validFilterOptions;
};

export const filterDirectionFromStatus = (
  status: string | undefined,
  directions: { label: string; value: string | undefined }[],
) => {
  switch (status) {
    case "PENDING":
      return directions.filter((direction) => direction.value !== "outgoing");
    case "SENT":
      return directions.filter((direction) => direction.value !== "incoming");
    default:
      return directions;
  }
};

const statusToTextOutgoing: Record<TransactionWrap["status"], string> = {
  NEW: "Ready to send",
  ACK: "Request received",
  SENT: "Request sent",
  ACCEPTED: "Accepted",
  REJECTED: "Rejected",
  DECLINED: "Declined",
  CANCELLED: "Cancelled",
  INCOMPLETE: "Missing Data",
  WAITING_FOR_INFORMATION: "Pending contact information",
  MISSING_BENEFICIARY_DATA: "Missing beneficiary data",
  WAITING_FOR_TR: "Waiting for TR",
  SAVED: "Stored",
  NOT_READY: "Not ready to respond",
  MERGED: "Merged",
};

const statusToTextIncoming: Record<TransactionWrap["status"], string> = {
  NEW: "Ready to send",
  ACK: "Address verified",
  SENT: "New transfer",
  ACCEPTED: "Accepted",
  REJECTED: "Rejected",
  DECLINED: "Declined",
  CANCELLED: "Cancelled",
  INCOMPLETE: "Missing Data",
  WAITING_FOR_INFORMATION: "Pending contact information",
  MISSING_BENEFICIARY_DATA: "Missing beneficiary data",
  WAITING_FOR_TR: "Waiting for TR",
  SAVED: "Stored",
  NOT_READY: "Not ready to respond",
  MERGED: "Merged",
};

const actionToTextOutgoing: Record<TransactionActionType, string> = {
  create: "Ready to send",
  confirm: "Request received",
  approve: "Request sent",
  accept: "Accepted",
  reject: "Rejected",
  decline: "Declined",
  cancel: "Cancelled",
  update: "Updated",
  redirect: "Redirected",
  notify: "Missing Data",
  receive: "Received",
  not_ready: "Not ready to respond",
  send_to_inbox: "",
};

const actionToTextIncoming: Record<TransactionActionType, string> = {
  create: "New transfer",
  confirm: "Address verified",
  approve: "Request received",
  accept: "Accepted",
  reject: "Rejected",
  decline: "Declined",
  cancel: "Cancelled",
  update: "Updated",
  redirect: "Redirected",
  notify: "Missing Data",
  receive: "Received",
  not_ready: "Not ready to respond",
  send_to_inbox: "",
};

export const getStatusText = (transaction: TransactionWrap, direction: Direction) => {
  const { status } = transaction;
  switch (direction) {
    case "incoming":
      return statusToTextIncoming[status];
    case "outgoing":
      return statusToTextOutgoing[status];
    default:
      return statusToTextOutgoing[status];
  }
};

export const getActionText = (action: TransactionActionType, direction: Direction) => {
  switch (direction) {
    case "incoming":
      return actionToTextIncoming[action];
    case "outgoing":
      return actionToTextOutgoing[action];
    default:
      return actionToTextOutgoing[action];
  }
};

type Progress = { type: "success" | "danger" | "warning" | "dark"; value: string };

export const getProgress = ({
  status,
  action,
  businessDaysSinceRequestForInfo,
  hasRequestTrackingForMissingDataSetting,
}: {
  status?: TransactionWrap["status"];
  action?: TransactionActionType;
  businessDaysSinceRequestForInfo?: number;
  hasRequestTrackingForMissingDataSetting?: boolean;
}): Progress => {
  if (status) {
    switch (status) {
      case "NEW":
        return { type: "success", value: "25" };
      case "SAVED":
      case "ACCEPTED":
        return { type: "success", value: "100" };
      case "INCOMPLETE":
        return getIncompleteProgress(businessDaysSinceRequestForInfo, hasRequestTrackingForMissingDataSetting);
      case "WAITING_FOR_TR":
      case "WAITING_FOR_INFORMATION":
      case "MISSING_BENEFICIARY_DATA":
        return { type: "warning", value: "25" };
      case "SENT":
        return { type: "success", value: "50" };
      case "ACK":
        return { type: "success", value: "75" };
      case "NOT_READY":
        return { type: "dark", value: "100" };
      case "CANCELLED":
      case "DECLINED":
      case "REJECTED":
        return { type: "danger", value: "100" };
      default:
        return { type: "success", value: "0" };
    }
  } else {
    switch (action) {
      case "create":
        return { type: "success", value: "25" };
      // case "SAVED":
      case "accept":
        return { type: "success", value: "100" };
      case "notify":
        return { type: "warning", value: "40" };
      case "approve":
        return { type: "success", value: "50" };
      case "confirm":
        return { type: "success", value: "75" };
      case "not_ready":
        return { type: "dark", value: "100" };
      case "cancel":
      case "decline":
      case "reject":
        return { type: "danger", value: "100" };
      default:
        return { type: "success", value: "0" };
    }
  }
};

export const getIncompleteProgress = (
  businessDaysSinceRequestForInfo: number | undefined,
  hasRequestTrackingForMissingDataSetting?: boolean,
): Progress => {
  if (businessDaysSinceRequestForInfo === undefined || !hasRequestTrackingForMissingDataSetting) {
    return { type: "warning", value: "25" };
  }

  const requestStatus = getRequestStatusAndProgress(businessDaysSinceRequestForInfo);

  return { type: requestStatus.kind, value: requestStatus.progressBar.toString() };
};
